import { faCircleQuestion } from '@fortawesome/free-regular-svg-icons';
import {
  faArrowsRotate,
  faAt,
  faCirclePlus,
  faFloppyDisk,
  faTrashCan
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Flex, Line } from 'components';
import { H2, P } from 'components/Typography/Typography';
import {
  Button,
  FieldLabel,
  Select,
  SwitchButton,
  TextField
} from 'components/_form';
import { ColorPicker } from 'components/_form/ColorPicker/ColorPicker';
import { t } from 'i18next';
import { useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { Tooltip } from 'react-tooltip';
import { toast } from 'react-toastify';
import {
  createPartnerCompany,
  editPartnerCompany,
  deletePartnerCompany,
  patchMainCompanySettings
} from 'api/companies';
import { companyKeys } from 'api/queryKeys';
import { useMainCompanyQuery } from 'hooks/queries/useMainCompanyQuery';
import { useLanguagesQuery } from 'hooks/queries/useLanguagesQuery';
import { useLocalesQuery } from 'hooks/queries/useLocalesQuery';
import { useDateFormatsQuery } from 'hooks/queries/useDateFormatsQuery';
import { ICompany } from 'types/company';
import { IOption } from 'components/_form/Select/Select';
import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { SUPPORTED_LANGUAGES } from 'types/language';
import { Controller } from 'react-hook-form';
import {
  FIRST_DAY_OF_WEEK_CODES,
  MEASUREMENT_UNIT_CODES
} from 'constants/companyLocalization';

type DefaultSettingsFormValues = typeof defaultSettingsValues & {
  mainCompany: {
    email_domain: string;
    user_code: string;
  };
  // Avoid recursive ICompany typing (partner_companies: ICompany[]).
  partnerCompanies: Array<Record<string, unknown> & { fieldId?: string }>;
  calendarLocalization: Array<Record<string, unknown>>;
};

const defaultSettingsValues = {
  session_duration_minutes: 60,
  use_only_company_emails: false,
  use_second_name: false,
  default_language_code: '',
  available_language_codes: [] as string[],
  id_locale: '',
  date_format: '',
  currency: '',
  currency_symbol: '',
  decimal_separator: '',
  thousands_separator: '',
  first_day_of_week: '',
  measurement_units: ''
};

export const DefaultSettingsForm = () => {
  const queryClient = useQueryClient();
  const { control, register, setValue, getValues, watch } = useForm<DefaultSettingsFormValues>({
    defaultValues: defaultSettingsValues
  });
  const defaultLanguageCodeFromForm = watch('default_language_code');
  const { data: mainCompany = null } = useMainCompanyQuery();
  const { data: languagesFromApi } = useLanguagesQuery();
  const languageCodesForLocales = useMemo(() => {
    const fromCompany = mainCompany?.available_language_codes?.length
      ? mainCompany.available_language_codes
      : mainCompany?.default_language_code
        ? [mainCompany.default_language_code]
        : [];
    const set = new Set([...fromCompany, defaultLanguageCodeFromForm].filter(Boolean));
    return set.size ? Array.from(set) : undefined;
  }, [
    mainCompany?.available_language_codes,
    mainCompany?.default_language_code,
    defaultLanguageCodeFromForm
  ]);
  const { data: localesFromApi = [] } = useLocalesQuery(languageCodesForLocales);
  const { data: dateFormatsFromApi = [] } = useDateFormatsQuery();

  const languageOptions = useMemo<IOption[]>(
    () =>
      (languagesFromApi?.length ? languagesFromApi : SUPPORTED_LANGUAGES).map((l) => ({
        value: l.code,
        label: String(
          t(`translationsView.tableHeaders.${String(l.code).trim().toLowerCase()}`, {
            defaultValue: l.name || l.code
          })
        )
      })),
    [languagesFromApi, t]
  );

  const localeOptions = useMemo<IOption[]>(
    () =>
      localesFromApi.map((loc) => ({
        value: loc.code,
        label: loc.code
      })),
    [localesFromApi]
  );

  const dateFormatOptions = useMemo<IOption[]>(
    () =>
      dateFormatsFromApi.map((df) => ({
        value: df.value,
        label: df.label
      })),
    [dateFormatsFromApi]
  );

  // Wysyłane do API: kody z FIRST_DAY_OF_WEEK_CODES. Label: common.<code>.
  const firstDayOfWeekOptions = useMemo<IOption[]>(
    () =>
      FIRST_DAY_OF_WEEK_CODES.map((code) => ({
        value: code,
        label: t(`common.${code}`) as string
      })),
    [t]
  );

  // Wysyłane do API: kody z MEASUREMENT_UNIT_CODES. Label: common.<code>.
  const measurementUnitsOptions = useMemo<IOption[]>(
    () =>
      MEASUREMENT_UNIT_CODES.map((code) => ({
        value: code,
        label: t(`common.${code}`) as string
      })),
    [t]
  );

  const getDefaultLocaleForLanguage = (languageCode: string | null | undefined) => {
    if (!languageCode) return undefined;
    return (
      localesFromApi.find(
        (loc) => loc.language_code === languageCode && loc.default
      ) ?? localesFromApi.find((loc) => loc.language_code === languageCode)
    );
  };

  const {
    fields: partnerCompaniesFields,
    append: partnerCompaniesAppend,
    update: partnerCompaniesUpdate,
    replace: partnerCompaniesReplace,
    remove: partnerCompaniesRemove
  } = useFieldArray({
    name: 'partnerCompanies',
    control,
    keyName: 'fieldId'
  });

  const {
    fields: calendarLocalizationFields,
    append: calendarLocalizationAppend,
    remove: calendarLocalizationRemove
  } = useFieldArray({
    name: 'calendarLocalization',
    control
  });

  useEffect(() => {
    if (mainCompany) {
      setValue('mainCompany.email_domain', mainCompany.email_domain || '');
      setValue('mainCompany.user_code', mainCompany.user_code || '');
      setValue('default_language_code', mainCompany.default_language_code || '');
      setValue('available_language_codes', mainCompany.available_language_codes || []);
      setValue(
        'id_locale',
        mainCompany.id_locale || ''
      );
      setValue('date_format', mainCompany.date_format || '');
      setValue('currency', mainCompany.currency || '');
      setValue('currency_symbol', mainCompany.currency_symbol || '');
      setValue('decimal_separator', mainCompany.decimal_separator || '');
      setValue('thousands_separator', mainCompany.thousands_separator || '');
      setValue('first_day_of_week', mainCompany.first_day_of_week || '');
      setValue('measurement_units', mainCompany.measurement_units || '');
      setValue(
        'session_duration_minutes',
        mainCompany.session_duration_minutes ?? 60
      );
      setValue('use_only_company_emails', mainCompany.use_only_company_emails ?? false);
      setValue('use_second_name', mainCompany.use_second_name ?? false);

      if (mainCompany.partner_companies && mainCompany.partner_companies.length > 0) {
        partnerCompaniesReplace(mainCompany.partner_companies as unknown as DefaultSettingsFormValues['partnerCompanies']);
      } else {
        partnerCompaniesReplace([
          {
            full_name: '',
            service_description: '',
            email_domain: '',
            user_code: ''
          }
        ] as DefaultSettingsFormValues['partnerCompanies']);
      }
    }
  }, [mainCompany, setValue, partnerCompaniesReplace]);

  // dodanie nowego partnera na liście formularza
  const addNewPartnerCompaniesField = () => {
    // dodanie nowego partnera do formularza jako pusty wiersz
    partnerCompaniesAppend({
      full_name: '',
      service_description: '',
      email_domain: '',
      user_code: ''
    } as unknown as DefaultSettingsFormValues['partnerCompanies'][number]);
  };

  const handleDeletePartner = (index: number) => {
    const partner = partnerCompaniesFields[index] as any;

    if (!partner.id) {
      // Niezapisany partner - usuń z listy bez potwierdzenia
      partnerCompaniesRemove(index);
    } else {
      // Zapisany partner - wymagaj potwierdzenia przed usunięciem z bazy
      const confirmMessage = t('administration.deletePartnerConfirm');
      if (confirm(confirmMessage)) {
        deletePartnerCompany(partner.id).then(() => {
          partnerCompaniesRemove(index);
          toast.success(t('common.success'));
        }).catch((error) => {
          console.error(error);
          toast.error(t('administration.deletePartnerError'));
        });
      }
    }
  }

  const handleSavePartner = (index: number) => {
    const partner = partnerCompaniesFields[index] as any;
    const values = getValues(`partnerCompanies.${index}`) as Partial<ICompany>;
    const updatedData = { ...partner, ...values };

    if (!partner.id) {
      if (!updatedData.full_name?.trim() || !mainCompany?.id) {
        toast.error(t('administration.companyName') || 'Wypełnij nazwę firmy');
        return;
      }
      createPartnerCompany({
        company: {
          full_name: updatedData.full_name,
          short_name: updatedData.full_name,
          email_domain: updatedData.email_domain,
          user_code: updatedData.user_code,
          service_description: updatedData.service_description
        }
      })
        .then((response) => {
          const savedData = {
            ...updatedData,
            id: response.data.id,
            short_name: response.data.short_name
          };
          partnerCompaniesUpdate(index, savedData);
          toast.success(t('common.success'));
        })
        .catch((error) => {
          console.error(error);
          toast.error(t('common.anErrorOccurred'));
        });
    } else {
      editPartnerCompany(partner.id, { company: updatedData })
        .then(() => {
          partnerCompaniesUpdate(index, updatedData);
          toast.success(t('common.success'));
        })
        .catch((error) => {
          console.error(error);
          toast.error(t('common.anErrorOccurred'));
        });
    }
  };

  const patchCompanyFields = (
    company: Parameters<typeof patchMainCompanySettings>[0]['company']
  ) => {
    if (!mainCompany?.id) return;
    patchMainCompanySettings({ company })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: companyKeys.main() });
        toast.success(t('common.success'));
      })
      .catch((error) => {
        console.error(error);
        toast.error(t('common.anErrorOccurred'));
      });
  };

  /** Zapisz – tylko pola górnej sekcji (język, sesja, flagi). */
  const handleSaveMainSettings = () => {
    const default_language_code = getValues('default_language_code') || null;
    const available_language_codes = getValues('available_language_codes') || [];
    const session_duration_minutes = getValues('session_duration_minutes');
    const n = Number(session_duration_minutes);
    const sessionMinutes =
      n > 0 && Number.isFinite(n) ? n : null;
    patchCompanyFields({
      default_language_code,
      available_language_codes,
      session_duration_minutes: sessionMinutes,
      use_only_company_emails: !!getValues('use_only_company_emails'),
      use_second_name: !!getValues('use_second_name')
    });
  };

  /** Zapisz – tylko kody użytkowników i domeny (własna firma). */
  const handleSaveUserCodesAndDomains = () => {
    patchCompanyFields({
      email_domain: getValues('mainCompany.email_domain'),
      user_code: getValues('mainCompany.user_code')
    });
  };

  /** Zapisz – tylko ustawienia lokalizacji (Locale ID itd.). */
  const handleSaveLocalization = () => {
    patchCompanyFields({
      id_locale: getValues('id_locale') || null,
      date_format: getValues('date_format') || null,
      currency: getValues('currency') || null,
      currency_symbol: getValues('currency_symbol') || null,
      decimal_separator: getValues('decimal_separator') || null,
      thousands_separator: getValues('thousands_separator') || null,
      first_day_of_week: getValues('first_day_of_week') || null,
      measurement_units: getValues('measurement_units') || null
    });
  };

  const handleRestoreDefaultLocalization = () => {
    const selectedLanguageCode = getValues('default_language_code');
    if (!selectedLanguageCode) {
      toast.error(t('administration.noDefaultLanguageForLocale'));
      return;
    }
    const defaultLocale = getDefaultLocaleForLanguage(selectedLanguageCode);
    if (!defaultLocale) {
      toast.error(t('administration.noDefaultLanguageForLocale'));
      return;
    }
    const confirmed = window.confirm(
      t('administration.restoreDefaultLocalizationConfirm') as string
    );
    if (!confirmed) return;
    setValue('id_locale', defaultLocale.code);
    setValue('date_format', defaultLocale.date_format || '');
    setValue('currency', defaultLocale.currency || '');
    setValue('currency_symbol', defaultLocale.currency_symbol || '');
    setValue('decimal_separator', defaultLocale.decimal_separator || '');
    setValue('thousands_separator', defaultLocale.thousands_separator || '');
    setValue('first_day_of_week', defaultLocale.first_day_of_week || '');
    setValue('measurement_units', defaultLocale.measurement_units || '');

    handleSaveLocalization();
  };

  const addNewCalendarLocalizationField = () => {
    calendarLocalizationAppend({});
  };

  return (
    <Flex flexDirection="column">
      <Flex p={4} flexDirection="column" width="100%">
        {/* Wiersz: Domyślny język */}
        <Flex alignItems="center" flexWrap="wrap" mb={3}>
          <Box width={['100%', '30%']} pr={3} flexShrink={0}>
            <P variant="body" fontWeight={700}>{t('administration.Default language for new users')}</P>
          </Box>
          <Flex width={['100%', '70%']} alignItems="center" flexWrap="nowrap">
            <Box flex="1" minWidth={0}>
              <Controller
                control={control}
                name="default_language_code"
                render={({ field: { value, onChange } }) => (
                  <Select
                    options={languageOptions}
                    selectedOptions={
                      value
                        ? languageOptions.filter((o) => o.value === value)
                        : undefined
                    }
                    onChange={(opt) => onChange(opt ? (opt as IOption).value : '')}
                    placeholder={t('administration.mainAppLanguage') as string}
                    withoutMargin
                  />
                )}
              />
            </Box>
            <Box ml={2} flexShrink={0}>
              <P variant="body" fontWeight={700}>– {t('administration.mainAppLanguage')}</P>
            </Box>
          </Flex>
        </Flex>

        {/* Wiersz: Języki interfejsu */}
        <Flex alignItems="center" flexWrap="wrap" mb={3}>
          <Box width={['100%', '30%']} pr={3} flexShrink={0}>
            <P variant="body" fontWeight={700}>{t('administration.Interface languages available')}</P>
          </Box>
          <Box width={['100%', '70%']}>
            <Controller
              control={control}
              name="available_language_codes"
              render={({ field: { value, onChange } }) => (
                <Select
                  isMulti
                  options={languageOptions}
                  selectedOptions={languageOptions.filter((o) => (value || []).includes(o.value))}
                  onChange={(val) => {
                    const arr = Array.isArray(val)
                      ? val.map((o) => o.value)
                      : val
                        ? [val.value]
                        : [];
                    onChange(arr);
                  }}
                  placeholder={t('administration.Interface languages available') as string}
                  withoutMargin
                />
              )}
            />
          </Box>
        </Flex>

        {/* Wiersz: Czas sesji */}
        <Flex alignItems="center" flexWrap="wrap" mb={3}>
          <Box width={['100%', '30%']} pr={3} flexShrink={0}>
            <P variant="body" fontWeight={700}>{t('administration.Default session duration (minutes)')}</P>
          </Box>
          <Box width={['100%', '70%']}>
            <Controller
              control={control}
              name="session_duration_minutes"
              render={({ field: { value, onChange } }) => (
                <TextField
                  type="number"
                  min={1}
                  value={value === undefined || value === null ? '' : String(value)}
                  onChange={(e) => {
                    const v = e.target.value;
                    onChange(v === '' ? 60 : Number(v));
                  }}
                />
              )}
            />
          </Box>
        </Flex>

        {/* Wiersz: Tylko e-mail firmowe */}
        <Flex alignItems="center" flexWrap="wrap" mb={3}>
          <Box width={['100%', '30%']} pr={3} flexShrink={0}>
            <P variant="body" fontWeight={700}>{t('administration.Only use corporate email addresses')}</P>
          </Box>
          <Flex width={['100%', '70%']} alignItems="center" flexWrap="wrap">
            <Controller
              control={control}
              name="use_only_company_emails"
              render={({ field: { value, onChange } }) => (
                <SwitchButton value={!!value} onChange={onChange} />
              )}
            />
            <Box
              ml={2}
              id={'OnlyUseCorporateEmailAddresses' + 'Tooltip'}
              data-tooltip-content={
                t('administration.OnlyUseCorporateEmailAddressesTooltip') as string
              }
            >
              <FontAwesomeIcon icon={faCircleQuestion} size="lg" />
            </Box>
            <Tooltip
              style={{ width: '90%' }}
              anchorId={'OnlyUseCorporateEmailAddresses' + 'Tooltip'}
            />
            <Box ml={2} display="inline-block">
              <Button
                type="button"
                disabled
                title={t('administration.check') as string}
                onClick={() => {
              }}
            >
              <Flex>
                <Box mr={2}>
                  <FontAwesomeIcon icon={faAt} />
                </Box>
                {t('administration.check')}
              </Flex>
            </Button>
            </Box>
          </Flex>
        </Flex>

        {/* Wiersz: Drugie imię */}
        <Flex alignItems="center" flexWrap="wrap" mb={4}>
          <Box width={['100%', '30%']} pr={3} flexShrink={0}>
            <P variant="body" fontWeight={700}>{t('administration.Uses users middle name')}</P>
          </Box>
          <Box width={['100%', '70%']}>
            <Controller
              control={control}
              name="use_second_name"
              render={({ field: { value, onChange } }) => (
                <SwitchButton value={!!value} onChange={onChange} />
              )}
            />
          </Box>
        </Flex>

        <Flex justifyContent="flex-end">
          <Button variant="eucalyptus" onClick={handleSaveMainSettings}>
            <FontAwesomeIcon icon={faFloppyDisk} /> {t('buttons.save')}
          </Button>
        </Flex>
      </Flex>

      <H2 mt={4} variant="h2" color="coloured" fontWeight={700}>
        {t('administration.USER CODES AND MAIL DOMAINS')}
      </H2>
      <Line />

      <Flex p={4} flexDirection="column">
        <Flex flexWrap="wrap" alignItems="flex-end">
          <Box width={['100%', '30%']} pb={2}>
            <strong style={{ fontSize: '13px' }}>
              {t('administration.Data for own users')}:
            </strong>
          </Box>
          <Box mr={3}>
            <TextField
              label="administration.Email domain"
              {...register('mainCompany.email_domain')}
            />
          </Box>
          <Box>
            <TextField
              label="administration.user code"
              {...register('mainCompany.user_code')}
            />
          </Box>
          <Box pl={1} pb={2}>
            <span style={{ fontSize: '12px', fontWeight: 700 }}>
              + {t('administration.next number')}
            </span>
          </Box>
          <Box ml={2}>
            <Button variant="eucalyptus" onClick={handleSaveUserCodesAndDomains}>
              <FontAwesomeIcon icon={faFloppyDisk} size="lg" />
            </Button>
          </Box>
        </Flex>

       <Box mt={4}>
          <strong style={{ fontSize: '15px' }}>
            {t('administration.DATA FOR PARTNERS AND SUBCONTRACTORS')}
          </strong>
          <Line variant="secondary" />
        </Box>

        {/* Wiersze partnerów – labelki w polach służą jako nagłówki; domyślnie jeden pusty wiersz */}
        {partnerCompaniesFields.map((field, index) => (
          <Flex key={field.fieldId} flexWrap={['wrap', 'nowrap']} mt={2}>
            <Box mr={2} flex="1" minWidth="200px">
              <TextField
                label="administration.companyName"
                {...register(`partnerCompanies.${index}.full_name`)}
              />
            </Box>
            <Box mr={2} flex="1" minWidth="200px">
              <TextField
                label="administration.Feature / Service"
                {...register(`partnerCompanies.${index}.service_description`)}
              />
            </Box>
            <Box mr={2} flex="1" minWidth="200px">
              <TextField
                label="administration.Email domain"
                {...register(`partnerCompanies.${index}.email_domain`)}
              />
            </Box>
            <Box flex="0 0 20%" minWidth="150px">
              <TextField
                label={`${t('administration.user code')} + ${t('administration.next number')}`}
                {...register(`partnerCompanies.${index}.user_code`)}
              />
            </Box>
            <Flex ml={2} alignItems="flex-end">
              <Box mr={2}>
                <Button
                  variant="eucalyptus"
                  onClick={() => handleSavePartner(index)}
                >
                  <FontAwesomeIcon icon={faFloppyDisk} size="lg" />
                </Button>
              </Box>
              <Button
                variant="redWhite"
                onClick={() => handleDeletePartner(index)}
              >
                <FontAwesomeIcon icon={faTrashCan} size="lg" />
              </Button>
            </Flex>
            <Box flex="0 0 50px" />
          </Flex>
        ))}

        {/* Przycisk Dodaj firmę - na dole pod wszystkimi wierszami */}
        <Flex justifyContent="end" mt={4}>
          <Button variant="eucalyptus" onClick={addNewPartnerCompaniesField}>
            <FontAwesomeIcon icon={faCirclePlus} /> {t('administration.addCompany')}
          </Button>
        </Flex>
      </Flex>

      <H2 mt={4} variant="h2" color="coloured">
        {t('administration.LOCATION')}
      </H2>
      <Line />

      <Flex px={4} justifyContent="space-between" alignItems="center">
        <P variant="body">
          {t(
            'The localization parameters set here override the default localizationThe localization parameters set here override the default localization settings assigned to the main application language.'
          )}
        </P>

        <Button variant="eucalyptus" onClick={handleRestoreDefaultLocalization}>
          <FontAwesomeIcon icon={faArrowsRotate} /> {t('profile.default')}
        </Button>
      </Flex>

      <Flex
        px={4}
        py={2}
        justifyContent="space-between"
        flexWrap="wrap"
        alignItems="end"
      >
        <Flex width={['100%', '32%']} flexDirection="column">
          <Flex width="100%">
            <Box width="50%" mt={2}>
              <P variant="body" fontWeight={700}>
                {t('administration.LocaleID')}
              </P>
            </Box>
            <Box width="50%">
              <Controller
                control={control}
                name="id_locale"
                render={({ field: { value, onChange } }) => (
                  <Select
                    options={localeOptions}
                    selectedOptions={
                      value
                        ? localeOptions.filter((o) => o.value === value)
                        : undefined
                    }
                    onChange={(opt) => onChange(opt ? (opt as IOption).value : '')}
                    placeholder={t('administration.LocaleID') as string}
                    withoutMargin
                  />
                )}
              />
            </Box>
          </Flex>

          <Flex width="100%">
            <Box width="50%" mt={2}>
              <P variant="body" fontWeight={700}>
                {t('profile.dateFormat')}
              </P>
            </Box>
            <Box width="50%">
              <Controller
                control={control}
                name="date_format"
                render={({ field: { value, onChange } }) => (
                  <Select
                    options={dateFormatOptions}
                    selectedOptions={
                      value
                        ? {
                            value,
                            label:
                              dateFormatOptions.find((o) => o.value === value)?.label || value
                          }
                        : undefined
                    }
                    onChange={(opt) => onChange(opt ? (opt as IOption).value : '')}
                    placeholder={t('profile.dateFormat') as string}
                    withoutMargin
                  />
                )}
              />
            </Box>
          </Flex>

          <Flex width="100%">
            <Box width="50%" mt={2}>
              <P variant="body" fontWeight={700}>
                {t('currency')}
              </P>
            </Box>
            <Box width="50%">
              <TextField {...register('currency')} />
            </Box>
          </Flex>
        </Flex>

        <Flex width={['100%', '32%']} flexDirection="column">
          <Flex width="100%">
            <Box width="50%" mt={4}>
              <P variant="body" fontWeight={700}>
                {t('profile.separators')}
              </P>
            </Box>
            <Box width="25%" mr={2}>
              <TextField label="profile.decimal" {...register('decimal_separator')} />
            </Box>
            <Box width="24%">
              <TextField label="profile.thousands" {...register('thousands_separator')} />
            </Box>
          </Flex>

          <Flex width="100%">
            <Box width="50%" mt={2}>
              <P variant="body" fontWeight={700}>
                {t('administration.currencySymbol')}
              </P>
            </Box>
            <Box width="50%">
              <TextField {...register('currency_symbol')} />
            </Box>
          </Flex>
        </Flex>

        <Flex width={['100%', '32%']} flexDirection="column">
          <Flex width="100%">
            <Box width="40%">
              <P variant="body" fontWeight={700}>
                {t('administration.First day of the week')}
              </P>
            </Box>
            <Box width="60%">
              <Controller
                control={control}
                name="first_day_of_week"
                render={({ field: { value, onChange } }) => (
                  <Select
                    options={firstDayOfWeekOptions}
                    selectedOptions={
                      value
                        ? firstDayOfWeekOptions.find((o) => o.value === value)
                        : undefined
                    }
                    onChange={(opt) => onChange(opt ? (opt as IOption).value : '')}
                  />
                )}
              />
            </Box>
          </Flex>

          <Flex width="100%">
            <Box width="40%">
              <P variant="body" fontWeight={700}>
                {t('administration.Measuring units')}
              </P>
            </Box>
            <Box width="60%">
              <Controller
                control={control}
                name="measurement_units"
                render={({ field: { value, onChange } }) => (
                  <Select
                    options={measurementUnitsOptions}
                    selectedOptions={
                      value
                        ? measurementUnitsOptions.find((o) => o.value === value)
                        : undefined
                    }
                    onChange={(opt) => onChange(opt ? (opt as IOption).value : '')}
                  />
                )}
              />
            </Box>
          </Flex>
        </Flex>
      </Flex>

      <Flex px={4} justifyContent="flex-end" py={2}>
        <Button variant="eucalyptus" onClick={handleSaveLocalization}>
          <FontAwesomeIcon icon={faFloppyDisk} /> {t('buttons.save')}
        </Button>
      </Flex>

      <Flex px={4} flexDirection="column">
        <Line variant="secondary" />

        <Flex
          pt={3}
          justifyContent="space-between"
          alignItems="center"
          flexDirection={['column', 'row']}
        >
          <Box width={['100%', '27%']}>
            <Select label="administration.Calendar of holidays and days off" options={[]} />
          </Box>
          <Box width="5%">
            <ColorPicker label="administration.color" />
          </Box>
          <Box width={['100%', '30%']}>
            <Select
              label="administration.area(s)"
              helperText={t('common.leave blank if all company')}
              options={[]}
            />
          </Box>

          <Box width={['100%', '10%']}>
            <Flex flexDirection="column" alignItems="center">
              <FieldLabel>{t('administration.Import holidays to the calendar')}</FieldLabel>
              <SwitchButton />
            </Flex>
          </Box>

          <Box py={2} width={['100%', '10%']}>
            <Button
              variant="eucalyptus"
              onClick={addNewCalendarLocalizationField}
            >
              <FontAwesomeIcon icon={faCirclePlus} /> {t('administration.add')}
            </Button>
          </Box>
        </Flex>

        {calendarLocalizationFields.map((field) => (
          <Flex
            key={field.id}
            pt={3}
            justifyContent="space-between"
            alignItems="center"
            flexDirection={['column', 'row']}
          >
            <Box width={['100%', '27%']}>
              <Select label="administration.Calendar of holidays and days off" options={[]} />
            </Box>
            <Box width="5%">
              <ColorPicker label="administration.color" />
            </Box>
            <Box width={['100%', '30%']}>
              <Select
                label="administration.area(s)"
                helperText={t('common.leave blank if all company')}
                options={[]}
              />
            </Box>

            <Box width={['100%', '10%']}>
              <Flex flexDirection="column" alignItems="center">
                <FieldLabel>{t('administration.Import holidays to the calendar')}</FieldLabel>
                <SwitchButton />
              </Flex>
            </Box>

            <Box
              py={2}
              width={['100%', '10%']}
              onClick={() => {
                calendarLocalizationRemove(0);
              }}
            >
              <Button variant="redWhite" bordered>
                <FontAwesomeIcon icon={faTrashCan} />
              </Button>
            </Box>
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
};
